import { Vector2 } from "../../../common/vector2.js";
import type { WinStreakTowerDto, WinStreakTowerPayloadDto } from "../../../api/dto/win_streak_tower.dto.js";
import { WinStreakTowerService } from "../../../win_streak_tower/win_streak_tower_service.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIScrollableContainer } from "../../common_ui_elements/ui_scrollable_container.js";
import { AdventureBackground } from "./adventure_background.js";
import { AdventureBoxOpenPopup } from "./adventure_box_open_popup.js";
import { AdventureFloorInfo, buildAdventureFloors } from "./adventure_floor_info.js";
import { AdventureHeader } from "./adventure_header.js";
import { AdventureProgressBar } from "./adventure_progress_bar.js";
import { AdventureTigerBasket } from "./adventure_tiger_basket.js";
import { AdventureTower } from "./adventure_tower.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { Game } from "../../../game.js";
import type { AdventureBoxReward } from "../../../adventure/adventure_box_rewards.js";
import { WinStreakTowerAnalytics } from "../../../win_streak_tower/win_streak_tower_analytics.js";

export type { AdventureFloorInfo };

export class AdventureScreen extends UIElement
{
  private readonly TOP_PADDING = 450;

  private events_controller: BasicEventsController | null = null;
  private scroll_container!: UIScrollableContainer;

  private readonly background: AdventureBackground;
  private readonly header: AdventureHeader;
  private readonly tiger_basket: AdventureTigerBasket;
  private readonly progress_bar: AdventureProgressBar;
  private readonly tower: AdventureTower;
  private readonly popup_layer: UIElementHolder;
  private readonly box_popup: AdventureBoxOpenPopup;
  private readonly period_start: string;
  private readonly period_version: number;
  private readonly period_end_ms: number;
  private rotation_refresh_inflight = false;
  private rotation_handled = false;
  private destroyed = false;
  private unsubscribe_tower: ( () => void ) | null = null;
  private reuse_key = "";
  private initial_scroll_offset = 0;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, tower_state: WinStreakTowerDto )
  {
    super( ctx, x, y, width, height, false, false );

    this.period_start = tower_state.period_start;
    this.period_version = tower_state.version;
    this.reuse_key = AdventureScreen.computeReuseKey( tower_state );
    const parsed_period_end = Date.parse( tower_state.period_end );
    this.period_end_ms = Number.isFinite( parsed_period_end ) ? parsed_period_end : 0;
    this.unsubscribe_tower = WinStreakTowerService.onChange( payload => this.onTowerPayloadChanged( payload ) );

    this.initializeEventsController( this.ctx.canvas );

    this.background = new AdventureBackground( ctx );
    this.header = new AdventureHeader( ctx );
    this.tiger_basket = new AdventureTigerBasket( ctx );
    this.progress_bar = new AdventureProgressBar( ctx );
    // Adventure itself is edge-to-edge, so the modal layer already receives
    // the complete physical canvas dimensions.
    this.popup_layer = new UIElementHolder(
      ctx,
      0,
      0,
      width,
      height,
      false
    );
    // An empty topmost holder otherwise consumes every touch before it reaches
    // the tower/header. It becomes modal only while its reward popup is open.
    this.popup_layer.setClickable( false );
    this.box_popup = new AdventureBoxOpenPopup( ctx, this.popup_layer );
    this.tower = new AdventureTower( ctx, async floor =>
    {
      if ( this.rotation_refresh_inflight || this.hasPeriodEnded() )
        return null;

      const response = await WinStreakTowerService.claimFloor( floor.id );
      if ( !response ) return null;
      this.popup_layer.setClickable( true );
      this.box_popup.open( response.rewards, {
        on_closed: () => this.popup_layer.setClickable( false ),
      } );
      return response.rewards;
    } );

    const floors = buildAdventureFloors( tower_state );
    const current_floor = floors.find( floor => floor.id === tower_state.current_floor_id );
    const last_floor = floors[floors.length - 1];
    const last_server_floor = tower_state.floors[tower_state.floors.length - 1];
    const reached_last_floor = tower_state.completed
      || last_server_floor?.reached === true
      || tower_state.current_floor_id === last_floor?.id;
    const current_level = reached_last_floor
      ? last_floor.level
      : ( current_floor?.level ?? 0 ) + tower_state.wins_progress;

    const intro_period_key = `${ tower_state.period_start }:${ tower_state.version }`;
    const should_play_intro = StatisticsStorage.load().adventure_intro_scroll_period !== intro_period_key;

    if ( should_play_intro )
    {
      StatisticsStorage.update( {
        adventure_intro_scroll_played: true,
        adventure_intro_scroll_period: intro_period_key,
      } );
    }

    this.createScene( floors, current_level, current_level, tower_state.period_end, should_play_intro );
    // Add the full-screen modal layer last so the reward chest stays above
    // the tower and header.
    this.addChild( this.popup_layer );
  }

  private createScene( floors: AdventureFloorInfo[], current_level: number, from_level: number, period_end: string, should_play_intro: boolean ): void
  {
    const viewport_h = this.rect.getH();
    const screen_w = this.rect.getW();
    // One centre section is needed for every server floor except the top one;
    // the zero floor is a separate section below them.
    const centre_count = Math.max( 0, floors.length - 1 );

    this.background.buildPinned( this, screen_w, viewport_h );

    const content_h = Math.max(
      viewport_h,
      AdventureTower.requiredContentHeight( centre_count ) + LayoutScaler.yFromDesign( this.TOP_PADDING )
    );

    this.scroll_container = new UIScrollableContainer(
      this.ctx, 0, 0, screen_w, viewport_h,
      {
        enable_momentum: true,
        enable_touch_scroll: true,
        scroll_sensitivity: 1.0,
        enable_rubber_band: true,
        rubber_band_resistance: 0.5,
        rubber_band_max_distance: 60,
        rubber_band_progression: 3.0,
      }
    );

    const content = new UIElementHolder( this.ctx, 0, 0, screen_w, content_h, false );

    this.background.buildGround( content, screen_w, content_h );
    this.tiger_basket.computeAnchor( screen_w, content_h );

    const badge_infos = this.tower.buildHouses( content, content_h, floors );
    this.progress_bar.build( content, badge_infos, current_level, from_level );
    this.tower.buildLevelBadges( content, badge_infos, current_level );

    this.scroll_container.addScrollableChild( content );
    this.scroll_container.setContentHeight( content_h );

    const marker_y = this.progress_bar.computeMarkerY( badge_infos, current_level );
    const preferred_marker_y = this.tiger_basket.preferredMarkerY( viewport_h );
    const max_scroll_offset = Math.max( 0, content_h - viewport_h );
    const marker_offset = marker_y !== null
      ? marker_y - preferred_marker_y
      : max_scroll_offset;
    const initial_offset = Math.max(
      0,
      Math.min( marker_offset, max_scroll_offset )
    );
    this.initial_scroll_offset = initial_offset;
    const tiger_marker_y = marker_y !== null
      ? marker_y - initial_offset
      : preferred_marker_y;
    const is_exact_floor = current_level > 0
      && badge_infos.some( info => info.floor_info.level === current_level );
    const tiger_bottom_y = this.tiger_basket.clampBottomY(
      this.tiger_basket.bottomForMarkerY( tiger_marker_y, is_exact_floor ),
      viewport_h
    );

    const INTRO_SCROLL_DELAY_MS = 300;
    const INTRO_SCROLL_DURATION_MS = 750;

    if ( should_play_intro )
    {
      this.scroll_container.scrollTo( 0, false );
      this.scroll_container.scrollTo( initial_offset, true, INTRO_SCROLL_DURATION_MS, INTRO_SCROLL_DELAY_MS );
    }
    else
    {
      this.scroll_container.scrollTo( initial_offset, false );
    }

    // Keep the tiger attached to the current progress point in content space.
    // Browsing the tower scrolls that point and the tiger together.
    const tiger_bottom_content_y = tiger_bottom_y + initial_offset;
    this.tiger_basket.build(
      content,
      screen_w,
      tiger_bottom_content_y
    );

    this.addChild( this.scroll_container );

    this.header.build( this, screen_w, period_end );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  private static computeReuseKey( tower_state: WinStreakTowerDto ): string
  {
    return JSON.stringify( { ...tower_state, seconds_until_reset: undefined } );
  }

  public canReuseScreen( opts: unknown ): boolean
  {
    const tower_state = ( opts as { tower_state?: WinStreakTowerDto } | undefined )?.tower_state;
    return !this.destroyed && !!tower_state && AdventureScreen.computeReuseKey( tower_state ) === this.reuse_key;
  }

  /** Debug: plays the floor-chest popup with fake rewards, no server claim. */
  public debugOpenBox( rewards: AdventureBoxReward[] ): void
  {
    this.popup_layer.setClickable( true );
    this.box_popup.open( rewards, {
      on_closed: () => this.popup_layer.setClickable( false ),
    } );
  }

  public onScreenHidden(): void
  {
    this.events_controller?.disable();
  }

  public onScreenShown(): void
  {
    this.events_controller?.enable();
    this.scroll_container?.scrollTo( this.initial_scroll_offset, false );
    WinStreakTowerAnalytics.opened( WinStreakTowerService.getCached()?.tower );
  }

  public destroy(): void
  {
    this.destroyed = true;
    this.unsubscribe_tower?.();
    this.unsubscribe_tower = null;
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  private onTowerPayloadChanged( payload: WinStreakTowerPayloadDto | null ): void
  {
    if ( this.destroyed || this.rotation_refresh_inflight || ScreenManager.getCurrentScreen() !== Screens.ADVENTURE || ScreenManager.getCurrentUi() !== this )
      return;
    const tower = WinStreakTowerService.isShowable( payload ) ? payload.tower : null;
    if ( !tower || AdventureScreen.computeReuseKey( tower ) === this.reuse_key )
      return;
    void ScreenManager.showScreen( Screens.ADVENTURE, {
      tower_state: tower,
      tower_state_preloaded: true,
    } );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );

    this.startRender();
    this.finishRender();
  }

  public update( now: number ): void
  {
    if ( this.background.updateClouds( now ) )
      this.setShouldBeRendered();

    this.header.update( now );
    this.updateBasic( now );

    if ( !this.rotation_handled && this.hasPeriodEnded() )
    {
      this.rotation_handled = true;
      void this.refreshAfterRotation();
    }
  }

  private hasPeriodEnded(): boolean
  {
    return this.period_end_ms <= 0 || Date.now() >= this.period_end_ms;
  }

  private async refreshAfterRotation(): Promise<void>
  {
    if ( this.rotation_refresh_inflight )
      return;

    this.rotation_refresh_inflight = true;
    const payload = await WinStreakTowerService.fetch( true );

    if ( this.destroyed || ScreenManager.getCurrentScreen() !== Screens.ADVENTURE )
      return;

    const next_tower = payload?.tower;
    const is_new_period = next_tower !== null && next_tower !== undefined && (
      next_tower.period_start !== this.period_start
      || next_tower.version !== this.period_version
    );
    const next_period_end = next_tower ? Date.parse( next_tower.period_end ) : NaN;
    const is_valid = payload?.enabled === true
      && next_tower !== null
      && next_tower !== undefined
      && next_tower.floors.length >= 2
      && is_new_period
      && Number.isFinite( next_period_end )
      && next_period_end > Date.now();

    if ( !is_valid )
    {
      await ScreenManager.showScreen( Screens.MENU );
      return;
    }

    await ScreenManager.showScreen( Screens.ADVENTURE, {
      tower_state: next_tower,
      tower_state_preloaded: true,
    } );
  }

  public name(): string
  {
    return "AdventureScreen";
  }
}
